/* eslint-disable no-console */
import dayjs from 'dayjs';
import { convertQueryCommandToQueryString, IQueryCommandOptions, ISearchCommandOptions } from '@llws/dynamic-router-utils';
import {
  convertCommandToQueryString,
  IApiResponse,
  IBaseFieldConfig,
  IExportFilterPayload,
  IFilterPayload,
  IGenericObject,
  IMetaResponse,
  IModifyPayload,
  IObjectWithTranslations,
  IRemovePayload,
  mergeQueryCommands,
  QueryStringCommandOptions,
  subsetObject,
  Nullable,
  IJunctionPostMetaResponse,
} from '@/shared';
import { downloadCSV } from '@/shared/helpers/download-csv';
import { adminRequest } from '@/shared/request-configs/admin-request';
import { IStoreBaseImplementationConstruct } from '@/shared/interfaces/store-base-implementation-construct';
import { IAction, IStateStrict } from '@/shared/interfaces/store-base';
import { INewTranslationSettingsStoreConfig } from '@/shared/interfaces/new-translation-settings';
import { getTranslationsByClientIdAndModel, saveTranslationsInObjects } from '@/shared/services/translations';
import { translationFormatter } from '@/shared/helpers/translation-formatter';
import vm from '@/main';

export class StoreBaseImplementation<DataObjectType extends Object> {
  /**
   * StoreBaseImplementation Class Constructor
   * @param {IStoreBaseImplementationConstruct} config - to set up
   * @constructor
   * @example ```
   * const base = new StoreBaseImplementation<ISuiteTypes>({
   *   adminGatewayResourcePath,
   *   defaultGetSingleFilters,
   *   defaultGetListFilters,
   *   showLogging,
   * });
   * ```
   */
  constructor(config: IStoreBaseImplementationConstruct) {
    if (config.showLogging && window.APP_CONFIG?.VUE_APP_ENV === 'development') {
      this.showLogging = config.showLogging;
    }

    this.resourcePath = config.adminGatewayResourcePath || '';
    this.translationSettings = config.translationSettings;
    this.dynamicRouterVersion = config.dynamicRouterVersion ?? 'v1';

    if (this.resourcePath) {
      this.defaultGetListFilters = { ...config.defaultGetListFilters && config.defaultGetListFilters };
      this.defaultGetSingleFilters = { ...config.defaultGetSingleFilters && config.defaultGetSingleFilters };
      this.listViewName = config.listViewName || '';
    }
  }

  public namespaced: boolean = true;
  public showLogging: boolean = false;
  public resourcePath: string = '';
  public listViewName: string = '';
  public defaultGetSingleFilters: QueryStringCommandOptions = {};
  public defaultGetListFilters: QueryStringCommandOptions = {};
  public translationSettings?: INewTranslationSettingsStoreConfig = undefined;
  public dynamicRouterVersion: 'v1' | 'v2' = 'v1';

  /**
   * Initial State of the Base Store
   * @example ```
   * export default {
   *   state: {
   *     ...base.state,
   *     floorplanResponse: null,
   *   } as IUnitCustomState
   * }
   * ```
   */
  public state: IStateStrict<DataObjectType> = {
    list: undefined,
    single: undefined,
  };

  /**
   * Initial Getters of the Base Store
   * @example ```
   * export default {
   *  getters: {
   *     ...base.getters,
   *     floorplanResponse: (state: IUnitCustomState) => [ ...(state.floorplanResponse?.data || []) ],
   *   }
   * }
   * ```
   */
  public getters = {
    /**
     * List Data Getter <br>
     * @param {IStateStrict<DataObjectType>} state - state from which get list.data
     * @returns {DataObjectType[]} - either state.list.data or an empty array
     */
    list: (
      state: IStateStrict<DataObjectType>,
    ): DataObjectType[] => {
      if (this.showLogging) console.log('--> STOREBASE - getters/list');
      return state.list ? [ ...state.list.data as DataObjectType[] ] : [];
    },

    /**
     * List Meta Getter <br>
     * @param {IStateStrict<DataObjectType>} state - state from which get list.meta
     * @returns {(IMetaResponse | undefined)} - either state.list.meta or undefined
     */
    listMeta: (
      state: IStateStrict<DataObjectType>,
    ): IMetaResponse | undefined => {
      if (this.showLogging) console.log('--> STOREBASE - getters/listMeta');
      return state.list && { ...state.list.meta as IMetaResponse };
    },

    /**
     * List Getter <br>
     * @param {IStateStrict<DataObjectType>} state - state from which get list
     * @returns {(IApiResponse<DataObjectType[], IMetaResponse> | undefined)} - either state.list or undefined
     */
    listResponse: (
      state: IStateStrict<DataObjectType>,
    ): IApiResponse<DataObjectType[], IMetaResponse> | undefined => {
      if (this.showLogging) console.log('--> STOREBASE - getters/listResponse');
      return state.list ? { ...state.list } : undefined;
    },

    /**
     * Single Getter <br>
     * @param {IStateStrict<DataObjectType>} state - state from which get single.data
     * @returns {(DataObjectType | undefined)} - either state.single.data or undefined
     */
    single: (
      state: IStateStrict<DataObjectType>,
    ): DataObjectType | undefined => {
      if (this.showLogging) console.log('--> STOREBASE - getters/single');
      return state.single ? { ...state.single.data } as DataObjectType : undefined;
    },
  };

  /**
   * Initial Mutations of the Base Store
   * @example ```
   * export default {
   *   mutations: {
   *     ...base.mutations,
   *
   *     floorplanResponse: (state: IUnitCustomState, payload: IApiResponse<ISuiteTypesFloorplans[], IMetaResponse>) => {
   *       state.floorplanResponse = payload;
   *     },
   *   }
   * }
   * ```
   */
  public mutations = {
    /**
     * Set Mutation <br>
     * Assigns Data from Payload to List & Single properties of State
     * @param {IStateStrict<DataObjectType>} state
     * @param {(DataObjectType | DataObjectType[] | IApiResponse<DataObjectType | DataObjectType[], IMetaResponse>)} payload - to set
     */
    set: (
      state: IStateStrict<DataObjectType>,
      payload: DataObjectType | DataObjectType[] | IApiResponse<DataObjectType | DataObjectType[], IMetaResponse>,
    ) => {
      let logOperation: string = '--> STOREBASE - mutations/set ';

      if (payload && Object.prototype.hasOwnProperty.call(payload, 'data')) {
        if (Array.isArray((payload as any).data)) {
          state.list = payload as IApiResponse<DataObjectType[], IMetaResponse>;
          logOperation += '- list: { data, meta } ';
        } else {
          state.single = payload as IApiResponse<DataObjectType, IMetaResponse>;
          logOperation += '- single: { data, meta } ';
        }
      } else if (Array.isArray(payload as any)) {
        state.list = {
          ...state.list && state.list,
          ...{ data: payload as DataObjectType[] },
        };
        logOperation += '- list: { data } ';
      } else {
        state.single = {
          ...state.single && state.single,
          ...{ data: payload as DataObjectType },
        };
        logOperation += '- single: { data } ';
      }

      if (this.showLogging) console.log(logOperation);
    },

    /**
     * Resets List property of state to undefined <br>
     * @param {IStateStrict<DataObjectType>} state
     * @param {boolean} [includeSingle] - optional, specifies if Single property of State should be reset as well
     */
    reset: (state: IStateStrict<DataObjectType>, includeSingle?: boolean) => {
      let logOperation: string = '--> STOREBASE - mutations/reset - list';

      state.list = undefined;

      if (includeSingle) {
        state.single = undefined;
        logOperation += ',single';
      }

      if (this.showLogging) console.log(logOperation);
    },

    /**
     * Resets Single property of State to undefined
     * @param {IStateStrict<DataObjectType>} state - to reset single property
     */
    resetSingle: (state: IStateStrict<DataObjectType>) => {
      state.single = undefined;
      if (this.showLogging) console.log('--> STOREBASE - mutations/resetSingle - single');
    },
  };

  /**
   * Initial Actions of the Base Store
   * @example ```
   *  export default {
   *   actions: {
   *     ...base.actions,
   *     getUnitFloorplansByUnitId: async (ctx: IAction<IStateStrict<ISuiteTypes>>, payload: { unitId: number }) => {
   *       ctx.commit('setFloorplanResponse', await adminRequest.get(`/property-api/units/${payload.unitId}/floorplans`));
   *     },
   *   }
   * }
   * ```
   */
  public actions = {
    /**
     * Get Action
     * @param {IAction<IStateStrict<DataObjectType>>} ctx - Action Context to Commit
     * @param {IFilterPayload} payload - for Data Manipulation
     * @returns {Promise<void>}
     */
    get: async <TId = number>(ctx: IAction<IStateStrict<DataObjectType>>, payload: IFilterPayload<TId>) => {
      if (this.showLogging) console.log(`--> STOREBASE - actions/get - ${(payload.id ? 'single' : 'list')}`);

      let result: IApiResponse<DataObjectType | DataObjectType[], IMetaResponse>;

      if (payload.id && !this.resourcePath.includes(':param')) {
        result = await this.getById(payload.id, payload.filters);
      } else if (payload.id && this.resourcePath.includes(':param')) {
        result = await this.getById(payload.id, payload.filters, payload.parentId!);
      } else if (!payload.id && this.resourcePath.includes(':param')) {
        result = await this.get(payload.filters, payload.parentId!);
      } else {
        result = await this.get(payload.filters);
      }

      if (this.translationSettings && payload.additional?.includeTranslations) {
        (result as any).data = await this.getTranslations(result.data as DataObjectType | DataObjectType[]);
      }

      if (!payload.id && payload.appendToList) {
        const current: DataObjectType[] = ctx.getters.list || [];
        result = {
          data: [ ...current, ...(result.data as unknown as DataObjectType[]) ],
          meta: result.meta,
        } as IApiResponse<DataObjectType[], IMetaResponse>;
      }

      ctx.commit('set', result);
    },

    /**
     * Search action, which is basically a `GET` request but with `POST`, where request body is the Dynamic Router Query Command object.
     * As part of `payload`, `alias` is minimum required parameter. The advantage to using the search action is that
     * we can have a much larger set of request filters (for example, `IN` statements with a lot of values). On top of that, there are no
     * reserved characters in wildcard searches using `LIKE` operators that would normally be stripped in query strings.
     *
     * Note that the endpoint you are using this action against must be using Dynamic Router V2+ and have the `hasSearchEndpoint`
     * boolean param toggled to true in its Route Configuration.
     * @param {IAction<IStateStrict<DataObjectType>>} ctx - Action Context to Commit
     * @param {ISearchCommandOptions} payload - for Data Filtering
     * @returns {Promise<void>}
     */
    search: async (ctx: IAction<IStateStrict<DataObjectType>>, payload: ISearchCommandOptions) => {
      const result = await adminRequest.post<DataObjectType[]>(`${this.resourcePath}/_search`, payload);
      ctx.commit('set', result);
      return result;
    },

    /**
     * Get CSV Action <br>
     * Downloads CSV from the payload, saves either as payload.filename_report.csv OR as current_date_report.csv
     * @param {IAction<IStateStrict<DataObjectType>>} ctx - Action Context
     * @param {IModifyPayload<number, DataObjectType[]>} payload - for Data Manipulation
     * @returns {Promise<IApiResponse<any, IMetaResponse>>}
     * @see StoreBaseImplementation.get
     */
    getCSV: async (
      ctx: IAction<IStateStrict<DataObjectType>>,
      payload: IExportFilterPayload,
    ): Promise<IApiResponse<any, IMetaResponse>> => {
      const result = await this.get(payload.filters, payload.parentId, payload.fields);

      if (payload.doDownload) {
        downloadCSV(typeof result === 'string' ? result : '', payload.filename || `${dayjs().format('DD-MM-YYYY')}_report.csv`);
      }
      return result;
    },
    /**
    * Save Action
     * @param {IAction<IStateStrict<DataObjectType>>} ctx - Action Context to Commit
     * @param {IModifyPayload<number, DataObjectType>} payload - for Data Manipulation
     * @returns {Promise<void>}
     */
    save: async <TId = number>(ctx: IAction<IStateStrict<DataObjectType>>, payload: IModifyPayload<TId, DataObjectType>) => {
      if (this.showLogging) console.log(`--> STOREBASE - actions/save - ${(payload.id ? 'update' : 'insert')}`);

      let { resourcePath } = this;
      let { data } = payload as IGenericObject;

      if (resourcePath.includes(':param')) {
        resourcePath = resourcePath.replace(':param', `${payload.parentId!}`);
      }

      if (this.translationSettings) {
        data = payload.id && data.languages
          ? await saveTranslationsInObjects<DataObjectType>(data, this.translationSettings.model)
          : this.formatNewTranslations(data);
      }

      const result = payload.id
        ? await adminRequest.put(`${resourcePath}/${payload.id!}`, subsetObject(data, ['languages']))
        : await adminRequest.post(resourcePath, subsetObject(data, ['languages']));
      const { single, list } = ctx.getters;
      const savedRecord = { ...single, ...data, ...(Array.isArray(result.data) ? result.data[0] : result.data) };

      ctx.commit('set', savedRecord);

      if (!vm()?.$rsGlobals.$isTranslateMode) {
        if (!payload.id) {
          list.unshift(savedRecord);
        } else {
          const idx = list.findIndex((v: IGenericObject) => v.id === savedRecord.id);

          if (idx !== -1) {
            list[idx] = { ...list[idx], ...savedRecord };
          }
        }

        ctx.commit('set', list);
      }
    },

    /**
     * Save Translations Action via {@link saveTranslationsInObjects}<br>
     * @param {IAction<IStateStrict<DataObjectType>>} ctx - Action Context to Commit
     * @param {(IObjectWithTranslations<DataObjectType>[] | IObjectWithTranslations<DataObjectType>)} payload - for Data Manipulation
     * @returns {Promise<void>}
     */
    saveTranslations: async (
      ctx: IAction<IStateStrict<DataObjectType>>,
      payload: IObjectWithTranslations<DataObjectType>[] | IObjectWithTranslations<DataObjectType>,
    ) => {
      if (!this.translationSettings) {
        console.warn('No translationSettings given to store base implementation. Skipping translation saving.');
      } else {
        const result = await saveTranslationsInObjects(payload, this.translationSettings.model);
        ctx.commit('set', result);
      }
    },

    /**
     * Insert Many Action via {@link adminRequest.post} <br>
     * @param {IAction<IStateStrict<DataObjectType>>} ctx - Action Context to Commit and get List Getter
     * @param {IModifyPayload<number, DataObjectType[]>} payload - for Data Manipulation
     * @returns {Promise<DataObjectType[]>}
     */
    insertMany: async (
      ctx: IAction<IStateStrict<DataObjectType>>,
      payload: IModifyPayload<number, DataObjectType[]>,
    ): Promise<DataObjectType[]> => {
      if (this.showLogging) console.log(`--> STOREBASE - actions/insertMany - ${payload.data.length} items`);

      let { resourcePath } = this;

      if (resourcePath.includes(':param')) {
        resourcePath = resourcePath.replace(':param', `${payload.parentId!}`);
      }

      const toInsert = (payload.data as DataObjectType[]).filter((v: any) => !v.id);
      const notInserted = (payload.data as DataObjectType[]).filter((v: any) => v.id);

      if (notInserted.length > 0) {
        /* eslint-disable-next-line */
        console.warn(`--> STOREBASE - actions/insertMany - ${notInserted.length} items with IDs. Skipping insert for these items.`);
      }

      const result: IApiResponse<DataObjectType[], null> = await adminRequest.post(
        `${resourcePath}`,
        toInsert.map((v) => subsetObject(v as Object, ['languages'])),
      );
      const { list } = ctx.getters;

      (result.data || []).forEach((d: any, idx: number) => {
        list.unshift({
          ...toInsert[idx],
          ...d,
          ...this.translationSettings && this.formatNewTranslations(d),
        });
      });

      ctx.commit('set', list);

      return result.data!;
    },

    /**
     * Update Many Action via {@link adminRequest.put} <br>
     * @param {IAction<IStateStrict<DataObjectType>>} ctx - Action Context to Commit and get List Getter
     * @param {IModifyPayload<number, DataObjectType[]>} payload - for Data Manipulation
     * @returns {Promise<DataObjectType[]>}
     */
    updateMany: async (
      ctx: IAction<IStateStrict<DataObjectType>>,
      payload: IModifyPayload<number|string, DataObjectType[]>,
    ): Promise<DataObjectType[]> => {
      if (this.showLogging) console.log(`--> STOREBASE - actions/updateMany - ${payload.data.length} items`);

      let { resourcePath } = this;

      if (resourcePath.includes(':param')) {
        resourcePath = resourcePath.replace(':param', `${payload.parentId!}`);
      }

      let toUpdate = (payload.data as DataObjectType[]).filter((v: any) => v.id);
      const notUpdated = (payload.data as DataObjectType[]).filter((v: any) => !v.id);

      if (notUpdated.length > 0) {
        /* eslint-disable-next-line */
        console.warn(`--> STOREBASE - actions/updateMany - ${notUpdated.length} items without IDs. Skipping update for these items.`);
      }

      if (this.translationSettings && toUpdate.some((v: any) => v.languages)) {
        toUpdate = await saveTranslationsInObjects<DataObjectType>(
          toUpdate as IObjectWithTranslations<DataObjectType>,
          this.translationSettings.model,
        ) as DataObjectType[];
      }

      const result: IApiResponse<DataObjectType[], null> = await adminRequest.put(
        `${resourcePath}`,
        toUpdate.map((v) => subsetObject(v as Object, ['languages'])),
      );

      const { list } = ctx.getters;

      (result.data || []).forEach((d: any) => {
        const idx = list.findIndex((v: IGenericObject) => v.id === d.id);

        if (idx !== -1) {
          list[idx] = {
            ...list[idx],
            ...toUpdate.find((v: any) => v.id === d.id),
            ...d,
          };
        }
      });

      ctx.commit('set', list);

      return result.data!;
    },

    /**
     * Replaces Many to Many State Action
     * @see adminRequest.post
     * @param {IAction<IStateStrict<DataObjectType>>} ctx - Action Context to Commit
     * @param {IRemovePayload} payload - for data manipulatons
     * @returns {Promise<void>}
     */
    replaceManyToManyState: async (ctx: IAction<IStateStrict<DataObjectType>>, payload: IModifyPayload<number, DataObjectType[]>) => {
      if (this.showLogging) console.log(`--> STOREBASE - actions/replaceManyToManyState`);

      let { resourcePath } = this;

      if (resourcePath.includes(':param')) {
        resourcePath = resourcePath.replace(':param', `${payload.parentId!}`);
      }

      const result = await adminRequest.post<unknown, IJunctionPostMetaResponse<unknown[]>>(`${resourcePath}`, Array.from(payload.data, (d) => (d as IGenericObject).id));
      ctx.commit('set', result.meta!.inverseRecords);
    },

    /**
     * Remove Action <br>
     * Deletes Payload from Store using async {@link adminRequest.delete} function
     * @param {IAction<IStateStrict<DataObjectType>>} ctx - Action Context to Commit and get Single & List Getters
     * @param {IRemovePayload} payload - for data manipulatons
     * @returns {Promise<void>}
     */
    remove: async <TId = string | number>(ctx: IAction<IStateStrict<DataObjectType>>, payload: IRemovePayload<TId>) => {
      if (this.showLogging) console.log(`--> STOREBASE - actions/remove`);

      let { resourcePath } = this;

      if (resourcePath.includes(':param')) {
        resourcePath = resourcePath.replace(':param', `${payload.parentId!}`);
      }

      await adminRequest.delete(resourcePath, [ payload.id ] as unknown as string[] | number[]);

      const { single, list } = ctx.getters;
      const idx = list.findIndex((v: any) => v.id === payload.id);

      if (idx !== -1) {
        list.splice(idx, 1);
        ctx.commit('set', list);
      }

      if (single?.id === payload.id) {
        ctx.commit('resetSingle');
      }
    },
  };

  public storeModuleExport = {
    namespaced: this.namespaced,
    state: this.state,
    getters: this.getters,
    mutations: this.mutations,
    actions: this.actions,
  };

  /**
   * @param {QueryStringCommandOptions} [paramFilters] - optional filters for parameters will be merged with {@link StoreBaseImplementation.defaultGetSingleFilters} into Filters Query Commands
   * @param {number} [parentId] - optional Parent ID of the object
   * @param {IBaseFieldConfig} [exportFields] - optional fields for CSV export
   * @private
   * @returns {Promise<IApiResponse>} - from {@link adminRequest.get}
   */
  private get<
    TResult extends any = any,
    TMeta extends Nullable<IMetaResponse> = IMetaResponse
  >(paramFilters?: QueryStringCommandOptions, parentId?: number, exportFields?: IBaseFieldConfig): Promise<IApiResponse<TResult, TMeta>> {
    const filters = this.convertCommand(mergeQueryCommands([this.defaultGetListFilters, paramFilters || {}]));

    const allFilters = filters + (
      this.listViewName
        ? `${filters !== '?' ? '&' : ''}view=${this.listViewName}`
        : ''
    );

    const url = parentId
      ? `${(this.resourcePath.replace(':param', `${parentId}`))}${allFilters}`
      : `${this.resourcePath}${allFilters}`;

    return (
      exportFields
        ? adminRequest.get<TResult, TMeta>(url, '', exportFields)
        : adminRequest.get<TResult, TMeta>(url)
    );
  }

  /**
   * Gets Object from the store by ID <br>
   * Used @ {@link StoreBaseImplementation.actions.get}
   * @param {number} id - ID of the object to get
   * @param {QueryStringCommandOptions} [paramFilters] - optional filters for parameters will be merged with {@link StoreBaseImplementation.defaultGetSingleFilters} into Filters Query Commands
   * @param {number} [parentId] - optional Parent ID of the object
   * @private
   * @returns {Promise<IApiResponse>} - from {@link adminRequest.get}
   */
  private getById<
    TResult extends any = any,
    TMeta extends Nullable<IMetaResponse> = IMetaResponse,
    TId = number,
  >(id: TId, paramFilters?: QueryStringCommandOptions, parentId?: number): Promise<IApiResponse<TResult, TMeta>> {
    const filters = this.convertCommand(mergeQueryCommands([this.defaultGetSingleFilters, paramFilters || {}]));

    return parentId
      ? adminRequest.get<TResult, TMeta>(`${(this.resourcePath.replace(':param', `${parentId}`))}/${id}${filters}`)
      : adminRequest.get<TResult, TMeta>(`${this.resourcePath}/${id}${filters}`);
  }
  /**
   * Formats New Translations <br> Used @ {@link StoreBaseImplementation.actions.save}, {@link StoreBaseImplementation.actions.insertMany}
   * @param {DataObjectType} data
   * @private
   * @returns {(IObjectWithTranslations<DataObjectType>) | IObjectWithTranslations<DataObjectType>[]}
   * @see IObjectWithTranslations
   */
  private formatNewTranslations(data: DataObjectType): IObjectWithTranslations<DataObjectType> | IObjectWithTranslations<DataObjectType>[] {
    const app = vm();
    return translationFormatter<DataObjectType>(
      data,
      [],
      app?.$rsGlobals.$siteSourceClientLanguages || [],
      { ...this.translationSettings!, ...{ clientId: app?.$rsGlobals.$siteSourceClientId! } },
    );
  }

  /**
   * Gets Translations if they persist @ {@link app.$rsGlobals.$siteSourceClientLanguages} <br>
   * If {@link StoreBaseImplementation.translationSettings} is undefined - the process is skipped <br>
   * Used @ {@link StoreBaseImplementation.actions.get}
   * @private
   * @returns {Promise<DataObjectType | DataObjectType[]>}
   * @param {(DataObjectType | DataObjectType[])} data
   */
  private async getTranslations(
    data: DataObjectType | DataObjectType[],
  ): Promise<DataObjectType | DataObjectType[]> {
    const app = vm();

    if (!this.translationSettings) {
      console.warn('No translationSettings given to store base implementation. Skipping translation retrieval.');
      return data;
    }

    if (app && app?.$rsGlobals.$siteSourceClientLanguages?.length > 0) {
      const translations = await getTranslationsByClientIdAndModel(
        app.$rsGlobals.$siteSourceClientId!,
        this.translationSettings.model,
      );
      return translationFormatter<DataObjectType>(
        data,
        translations,
        app?.$rsGlobals.$siteSourceClientLanguages,
        { ...this.translationSettings, ...{ clientId: app.$rsGlobals.$siteSourceClientId! } },
      ) as DataObjectType | DataObjectType[];
    }

    return data;
  }

  private convertCommand(query: QueryStringCommandOptions | IQueryCommandOptions) {
    return this.dynamicRouterVersion === 'v1'
      ? convertCommandToQueryString(query as QueryStringCommandOptions)
      : convertQueryCommandToQueryString(query as IQueryCommandOptions);
  }
}
